/**
 * TestCaseActivityLog Component
 *
 * Displays test case activity for a project (status changes, created, assigned)
 * with old → new status change visualization.
 */

import { useState } from 'react';
import {
  ClipboardCheck,
  Download,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { EmptyState } from '@/components/EmptyState';
import { toast } from '@/lib/toast';
import { formatTimestamp, formatRelativeTime } from '@/lib/utils';
import { useTestCaseLogs } from '@/hooks/useActivityLogs';
import type { TestCaseLogEntry } from '@/types/domain';

interface TestCaseActivityLogProps {
  projectId: string;
}

const actionConfig: Record<string, { label: string; className: string }> = {
  status_change: { label: 'Status Change', className: 'bg-info/10 text-info' },
  created: { label: 'Created', className: 'bg-success/10 text-success' },
  assigned: { label: 'Assigned', className: 'bg-primary/10 text-primary' },
};

function formatStatus(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function TestCaseLogRow({ entry }: { entry: TestCaseLogEntry }) {
  const config = actionConfig[entry.action] ?? actionConfig.status_change;

  return (
    <TableRow>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="space-y-0.5">
                <span className="text-sm">{formatTimestamp(entry.timestamp)}</span>
                <span className="block text-xs text-muted-foreground">{formatRelativeTime(entry.timestamp)}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {new Date(entry.timestamp).toLocaleString()}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={config.className}>
          {config.label}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">{entry.actor_name}</TableCell>
      <TableCell className="text-sm text-muted-foreground truncate max-w-[150px]" title={entry.scenario_name}>
        {entry.scenario_name}
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs font-mono">
          Step {entry.step_number}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">
        {entry.old_value && entry.new_value ? (
          <span className="flex items-center gap-1.5">
            <span className="text-muted-foreground">{formatStatus(entry.old_value)}</span>
            <span>→</span>
            <span className="font-medium">{formatStatus(entry.new_value)}</span>
          </span>
        ) : entry.new_value ? (
          <span className="font-medium">{formatStatus(entry.new_value)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

export function TestCaseActivityLog({ projectId }: TestCaseActivityLogProps) {
  const { data, isLoading } = useTestCaseLogs(projectId, { refetchInterval: 30000 });
  const [showAll, setShowAll] = useState(false);

  const handleExport = () => {
    const logs = data?.logs ?? [];
    const csv = [
      'Timestamp,Action,Actor,Scenario,Step,Old Status,New Status',
      ...logs.map(log =>
        `"${new Date(log.timestamp).toISOString()}","${log.action}","${log.actor_name}","${log.scenario_name}",${log.step_number},"${log.old_value ?? ''}","${log.new_value ?? ''}"`
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test-case-activity-${projectId}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Test case activity exported to CSV');
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Test Case Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const logs = data?.logs ?? [];

  if (logs.length === 0) {
    return (
      <EmptyState
        icon={ClipboardCheck}
        title="No Test Case Activity"
        description="Test case activity will appear here when statuses change."
      />
    );
  }

  const displayLogs = showAll ? logs : logs.slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-primary" />
              Test Case Activity
            </CardTitle>
            <CardDescription>
              {logs.length} test case event{logs.length !== 1 ? 's' : ''}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExport}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[160px]">Time</TableHead>
              <TableHead className="w-[120px]">Action</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Scenario</TableHead>
              <TableHead className="w-[80px]">Step</TableHead>
              <TableHead>Status Change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayLogs.map((entry, idx) => (
              <TestCaseLogRow key={`${entry.timestamp}-${idx}`} entry={entry} />
            ))}
          </TableBody>
        </Table>
        {logs.length > 5 && (
          <div className="mt-3 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAll(!showAll)}
              className="text-muted-foreground"
            >
              {showAll ? 'Show less' : `Show all ${logs.length} entries`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
